import { localeFor } from "./languages";
import type { Language } from "./languages";
import { ui } from "./localization";
import type { ChatMessage } from "../types";

export type ResourceCachePatch = Pick<ChatMessage, "generatedImages" | "generatedFiles" | "browserScreenshots"> & { sourceUrls: Record<string, string> };
const pendingDownloads = new Map<string, Promise<string>>();
const MAX_RESOURCE_BYTES = 30 * 1024 * 1024;
export const isTemporaryResourceUrl = (url: string): boolean => /^\/api\/(?:image-jobs|generated-files)\/[a-zA-Z0-9_./?=&%-]+$/.test(url);
export const isEmbeddedResourceUrl = (url: string): boolean => /^data:(?:image\/(?:png|jpeg|webp|gif)|application\/(?:pdf|json)|text\/(?:plain|markdown|csv))(?:;charset=[\w-]+)?;base64,[a-zA-Z0-9+/]*={0,2}$/.test(url);

export const downloadResource = (url: string): Promise<string> => {
  if (isEmbeddedResourceUrl(url)) return Promise.resolve(url);
  if (!isTemporaryResourceUrl(url)) return Promise.reject(new Error("unsupported_resource_url"));
  const existing = pendingDownloads.get(url); if (existing) return existing;
  const operation = (async () => {
    const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });
    if (!response.ok) throw new Error("resource_unavailable");
    const size = Number(response.headers.get("content-length"));
    if (Number.isFinite(size) && size > MAX_RESOURCE_BYTES) throw new Error("resource_too_large");
    const blob = await response.blob();
    if (blob.size > MAX_RESOURCE_BYTES) throw new Error("resource_too_large");
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(reader.error); reader.readAsDataURL(blob);
    });
    if (!isEmbeddedResourceUrl(dataUrl)) throw new Error("unsupported_resource_type");
    return dataUrl;
  })();
  pendingDownloads.set(url, operation);
  void operation.finally(() => { pendingDownloads.delete(url); }).catch(() => undefined);
  return operation;
};

export const cacheGeneratedResources = async (message: ChatMessage): Promise<{ patch: ResourceCachePatch; failed: number }> => {
  const patch: ResourceCachePatch = { sourceUrls: {} }; let failed = 0;
  // Sequential work bounds memory use when a message includes several large outputs.
  for (const field of ["generatedImages", "browserScreenshots"] as const) {
    for (const item of message[field] ?? []) {
      if (!isTemporaryResourceUrl(item.dataUrl)) continue;
      try {
        const local = { ...item, dataUrl: await downloadResource(item.dataUrl) };
        patch.sourceUrls[`${field}:${item.id}`] = item.dataUrl;
        if (field === "generatedImages") (patch.generatedImages ??= []).push(local as NonNullable<ChatMessage["generatedImages"]>[number]);
        else (patch.browserScreenshots ??= []).push(local as NonNullable<ChatMessage["browserScreenshots"]>[number]);
      } catch { failed += 1; }
    }
  }
  for (const file of message.generatedFiles ?? []) {
    if (!isTemporaryResourceUrl(file.downloadUrl)) continue;
    try {
      const { expiresAt: _expiry, ...local } = file;
      (patch.generatedFiles ??= []).push({ ...local, downloadUrl: await downloadResource(file.downloadUrl) });
      patch.sourceUrls[`generatedFiles:${file.id}`] = file.downloadUrl;
    } catch { failed += 1; }
  }
  return { patch, failed };
};

export const mergeCachedResources = (current: ChatMessage, patch: ResourceCachePatch): ChatMessage => {
  let result = current;
  for (const field of ["generatedImages", "browserScreenshots"] as const) {
    const updates = new Map((patch[field] ?? []).map((item) => [item.id, item]));
    if (!updates.size || !current[field]?.length) continue;
    const items = current[field]!.map((item) => {
      const cached = updates.get(item.id);
      return cached && item.dataUrl === patch.sourceUrls[`${field}:${item.id}`] ? { ...item, dataUrl: cached.dataUrl } : item;
    });
    if (items.some((item, index) => item !== current[field]![index])) result = { ...result, [field]: items };
  }
  if (patch.generatedFiles?.length && current.generatedFiles?.length) {
    const updates = new Map(patch.generatedFiles.map((item) => [item.id, item]));
    const files = current.generatedFiles.map((item) => {
      const cached = updates.get(item.id);
      if (!cached || item.downloadUrl !== patch.sourceUrls[`generatedFiles:${item.id}`]) return item;
      const { expiresAt: _expiry, ...rest } = item; return { ...rest, downloadUrl: cached.downloadUrl };
    });
    if (files.some((item, index) => item !== current.generatedFiles![index])) result = { ...result, generatedFiles: files };
  }
  return result;
};

export const fileAvailability = (expiresAt: string | undefined, language: Language, now = Date.now()): { expired: boolean; label: string } => {
  if (!expiresAt) return { expired: false, label: ui(language, "Available in this browser") };
  const remaining = Date.parse(expiresAt) - now;
  if (!Number.isFinite(remaining)) return { expired: true, label: ui(language, "Download expiry unknown") };
  if (remaining <= 0) return { expired: true, label: ui(language, "Temporary file expired") };
  const minutes = Math.ceil(remaining / 60_000), hours = Math.floor(minutes / 60), suffix = minutes % 60;
  const unit = (value: number, name: "hour" | "minute") => new Intl.NumberFormat(localeFor(language), { style: "unit", unit: name, unitDisplay: "short" }).format(value);
  const duration = language === "zh" ? `${hours ? `${hours} 小时${suffix ? ` ${suffix} 分钟` : ""}` : `${minutes} 分钟`}`
    : language === "en" ? `${hours ? `${hours}h${suffix ? ` ${suffix}m` : ""}` : `${minutes}m`}`
    : [hours ? unit(hours, "hour") : "", suffix || !hours ? unit(suffix || minutes, "minute") : ""].filter(Boolean).join(" ");
  return { expired: false, label: ui(language, "Expires in {0}", duration) };
};
